import { Cell, CellChange, Range, ReactGrid, ReactGridProps, TextCell } from '@silevis/reactgrid'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import sentry from 'utils/sentry/sentry'
import '@silevis/reactgrid/styles.css'

interface CustomReactGridProps extends ReactGridProps {
  onCopy?: (text: string) => void
  onPaste?: (changes: CellChange[]) => void
}
interface CellWithCopyText extends Cell {
  copyText?: string
}

type CustomCellMatrix = (Cell | CellWithCopyText)[][]

export function CustomReactGrid({ rows, columns, onCopy, onPaste, ...restProps }: CustomReactGridProps): ReactElement {
  const [selectedRanges, setSelectedRanges] = useState<Range[]>([])

  const handleSelectionChanged = useCallback((ranges: Range[]) => {
    if (ranges.length > 0) {
      setSelectedRanges([ranges[0]])
    } else {
      setSelectedRanges([])
    }
  }, [])

  const getCellsInRange = (rows: any[], columns: any[], range: Range): CustomCellMatrix => {
    const cellMatrix: CustomCellMatrix = []

    const firstRow = range.first.row
    const lastRow = range.last.row
    const firstCol = range.first.column
    const lastCol = range.last.column

    const firstRowIndex = rows.findIndex((r) => r.rowId === firstRow.rowId)
    const lastRowIndex = rows.findIndex((r) => r.rowId === lastRow.rowId)
    const firstColIndex = columns.findIndex((c) => c.columnId === firstCol.columnId)
    const lastColIndex = columns.findIndex((c) => c.columnId === lastCol.columnId)

    for (let r = firstRowIndex; r <= lastRowIndex; r += 1) {
      const cellRow: Cell[] = []
      cellMatrix.push(cellRow)

      for (let c = firstColIndex; c <= lastColIndex; c += 1) {
        const cell = rows[r]?.cells?.[c] || { type: 'text', text: '' }
        cellRow.push(cell)
      }
    }

    return cellMatrix
  }

  //Override default copy behavior to remove error when copying cells with å/ä/ö
  const handleCopy = useCallback(
    (e: ClipboardEvent) => {
      e.stopPropagation()
      e.preventDefault()

      if (!selectedRanges.length) return

      try {
        const range = selectedRanges[0]
        const selectedCells = getCellsInRange(rows, columns, range)
        const clipboardText = formatCellsForClipboard(selectedCells)

        e.clipboardData?.setData('text/plain', clipboardText)

        if (onCopy) onCopy(clipboardText)
      } catch (error) {
        sentry.captureException(error as Error, {
          level: 'warning',
          tags: {
            sentry_scope: 'custom_react_grid',
            issue_type: 'copy_failure',
          },
          extra: {
            rowCount: rows.length,
            columnCount: columns.length,
            selectedRangeCount: selectedRanges.length,
          },
          fingerprint: ['custom_react_grid', 'copy_failure'],
        })
      }
    },
    [rows, columns, selectedRanges, onCopy]
  )

  useEffect(() => {
    document.addEventListener('copy', handleCopy, true)
    return () => document.removeEventListener('copy', handleCopy, true)
  }, [handleCopy])

  const formatCellsForClipboard = (cellMatrix: CustomCellMatrix): string => {
    if (!cellMatrix.length) return ''
    return cellMatrix
      .map((row) =>
        row
          .map((cell) => {
            if ('text' in cell) {
              return (cell as TextCell).text
            } else if ('value' in cell && typeof cell.value === 'number') {
              return isNaN(cell.value) ? '' : cell.value.toString()
            }
            return ''
          })
          .join('\t')
      )
      .join('\n')
  }

  //Override default paste behavior for correct format of numbers
  const handlePaste = useCallback(
    (e: ClipboardEvent) => {
      if (!document.activeElement?.closest('.reactgrid') || !selectedRanges.length) return

      e.stopPropagation()
      e.preventDefault()

      try {
        const clipboardText = e.clipboardData?.getData('text/plain') || ''
        if (!clipboardText) return
        const clipboardRows = clipboardText.split(/\r\n|\n/)
        const clipboardCells = clipboardRows.map((row) => row.split('\t'))

        const range = selectedRanges[0]
        const firstRowIndex = rows.findIndex((r) => r.rowId === range.first.row.rowId)
        const firstColIndex = columns.findIndex((c) => c.columnId === range.first.column.columnId)

        if (firstRowIndex < 0 || firstColIndex < 0) return

        const changes: CellChange[] = []

        clipboardRows.forEach((_, rowOffset) => {
          const targetRowIndex = firstRowIndex + rowOffset
          if (targetRowIndex >= rows.length) return

          clipboardCells[rowOffset].forEach((value, colOffset) => {
            const targetColIndex = firstColIndex + colOffset
            if (targetColIndex >= columns.length) return

            const targetRow = targetRowIndex >= 0 && targetRowIndex < rows.length ? rows[targetRowIndex] : undefined
            const targetCell = targetRow?.cells[targetColIndex]
            const columnId = columns[targetColIndex].columnId

            if (targetRow && targetCell && targetCell.type === 'number') {
              let numericValue: number

              let cleanedValue = value.trim()

              // Handle both hyphen and minus signs for negatives, hyphen is common when using ctrl + X
              const isNegative = cleanedValue.startsWith('-') || cleanedValue.startsWith('−')

              // Remove everything except digits and decimal points
              // This handles thousands separators (commas, spaces) and currency symbols
              cleanedValue = cleanedValue.replace(/[^\d.-]/g, '')

              // Reapply negative sign if needed
              if (isNegative) {
                cleanedValue = '-' + cleanedValue.replace(/^-/, '') // Ensure only one leading minus
              }

              if (cleanedValue.includes('.')) {
                numericValue = parseFloat(cleanedValue)
              } else {
                numericValue = parseInt(cleanedValue, 10)
              }

              if (!isNaN(numericValue)) {
                changes.push({
                  rowId: targetRow.rowId,
                  columnId: columnId,
                  type: 'number',
                  newCell: { ...targetCell, value: numericValue, type: 'number' },
                  previousCell: { ...targetCell, type: 'number', value: NaN },
                })
              }
            } else if (targetRow && targetCell && targetCell.type === 'text') {
              changes.push({
                rowId: targetRow.rowId,
                columnId: columnId,
                type: targetCell.type,
                newCell: {
                  ...targetCell,
                  text: value,
                  type: targetCell.type,
                },
                previousCell: {
                  ...targetCell,
                  type: 'text',
                  text: '',
                },
              })
            }
          })
        })

        if (changes.length > 0 && onPaste) {
          onPaste(changes)
        }
      } catch (error) {
        sentry.captureException(error as Error, {
          level: 'warning',
          tags: {
            sentry_scope: 'custom_react_grid',
            issue_type: 'paste_failure',
          },
          extra: {
            rowCount: rows.length,
            columnCount: columns.length,
            selectedRangeCount: selectedRanges.length,
          },
          fingerprint: ['custom_react_grid', 'paste_failure'],
        })
      }
    },
    [rows, columns, selectedRanges, onPaste]
  )

  useEffect(() => {
    document.addEventListener('paste', handlePaste, true)
    return () => document.removeEventListener('paste', handlePaste, true)
  }, [handlePaste])

  return <ReactGrid {...restProps} rows={rows} columns={columns} onSelectionChanged={handleSelectionChanged} />
}
